import axios from 'axios'
import userToken from '../redux/userToken'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { LOGIN } from '../navigation/constants'
import { refreshSession } from '../services/refreshSession'

const qs = require('qs')

// Serialize nested objects
const requestInterceptor = function (config) {
  config.paramsSerializer = params => {
    return qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: true,
      strictNullHandling: true,
    })
  }
  if (userToken.token) {
    config.headers.Authorization = `Bearer ${userToken.token}`
  }
  return config
}

// Function that will be called to refresh authorization.
// Goes through refreshSession so this and isLogged() share ONE in-flight
// request: the Auth0 client uses refresh-token rotation with 10s leeway, and
// two concurrent refreshes look like token reuse, which revokes the whole family.
export const refreshAuthLogic = failedRequest =>
  refreshSession()
    .then(token => {
      failedRequest.response.config.headers['Authorization'] = 'Bearer ' + token
      return Promise.resolve()
    })
    .catch(() => {
      // `window.location = 'login'` resolved relative to the current path
      // (there is no <base> tag), so a refresh failure on
      // /warehouse/365/replenishments/detail/1220966 navigated to
      // .../detail/login, which matches the replenishment detail route with
      // id="login" instead of reaching the login page.
      window.location.assign(LOGIN)
      // Reject with the request that failed, not with nothing. Every caller's
      // `catch (error)` reads error.response.*, and rejecting bare handed them
      // undefined.
      return Promise.reject(failedRequest)
    })

// In-flight writes, so nothing can drop a page reload on top of one. Counted here and not through
// react-query's `useIsMutating` because almost nothing in this app goes through `useMutation` — the
// picking, packing and receiving screens call these services directly, often from a barcode-scanner
// callback, with no keystroke anywhere in this document to give them away.
const WRITE_METHODS = ['post', 'put', 'patch', 'delete']
// A write that never settles must not disable the caller forever, so entries expire. Well past any
// request this app makes, and only ever consulted as "is anything in flight right now".
const PENDING_WRITE_TTL_MS = 60 * 1000
const pendingWrites = new Map()
let nextWriteId = 0

export const hasPendingWrites = () => {
  const cutoff = Date.now() - PENDING_WRITE_TTL_MS
  pendingWrites.forEach((startedAt, id) => {
    if (startedAt < cutoff) pendingWrites.delete(id)
  })
  return pendingWrites.size > 0
}

const trackWrite = function (config) {
  if (WRITE_METHODS.includes(String(config.method).toLowerCase())) {
    // A replay after a 401 comes back through here carrying the same config, and its entry was
    // deliberately left standing (see untrackWrite). Take the entry over instead of allocating
    // a second id, or the original would be orphaned in the map until the TTL swept it and
    // would keep the tab ineligible for a full minute after every silent token refresh.
    if (!config.__pendingWriteId || !pendingWrites.has(config.__pendingWriteId)) {
      config.__pendingWriteId = ++nextWriteId
    }
    pendingWrites.set(config.__pendingWriteId, Date.now())
  }
  return config
}

// Both outcomes, or a failed write would be counted as in flight until its entry expires.
//
// With one exception: a 401 is not the end of this write. This interceptor runs before
// axios-auth-refresh's, which holds the rejection, renews the token through refreshSession — which
// never touches this instance — and only then replays the request. Deleting the entry here would
// make the counter read zero for that whole refresh round trip, and the tabs this counter exists
// for have been open across a deploy for hours, so their access token has expired and their first
// write is exactly the one that takes the 401 path. A navigation or a hidden-tab check landing
// inside that window would reload the page and drop the replay. The entry stands until the replay
// settles, and if the refresh fails instead the app redirects to the login page and the TTL bounds
// the leak.
const REFRESHABLE_STATUS = 401

const untrackWrite = (config, status) => {
  if (status === REFRESHABLE_STATUS) return
  if (config && config.__pendingWriteId) pendingWrites.delete(config.__pendingWriteId)
}

const baseURl = process.env.REACT_APP_PLATFORM_URL
const headers = {}
const token = userToken.token
if (token) {
  headers.Authorization = `Bearer ${token}`
}
const axiosInstance = axios.create({
  baseURL: baseURl,
  headers,
})
axiosInstance.interceptors.request.use(requestInterceptor)
axiosInstance.interceptors.request.use(trackWrite)
axiosInstance.interceptors.response.use(
  response => {
    untrackWrite(response.config)
    return response
  },
  error => {
    untrackWrite(error?.config, error?.response?.status)
    return Promise.reject(error)
  },
)
createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic)

export default axiosInstance
